/**
 * Journey section progress computation — inlined in sdk-react.
 *
 * Copied from lib/experience-specs/journey-sections.ts to avoid
 * importing @health/core in the browser SDK bundle (forbidden by
 * check-sdk-browser-surface).
 *
 * Pure functions, no side effects.
 */

// ---------------------------------------------------------------------------
// Types (mirror of lib/experience-specs/types.ts — only what we need)
// ---------------------------------------------------------------------------

export interface JourneySectionConfig {
  id: string;
  label: string;
  pendingText: string;
  groups: string[];
  summaryFields?: string[];
  appearsWhen?: "first_field" | "always";
}

export interface SpecField {
  id: string;
  group?: string;
  required?: boolean;
  choices?: Array<{ id: string; label?: string; text?: string }>;
  [key: string]: unknown;
}

export interface JourneySectionProgress {
  id: string;
  label: string;
  pendingText: string;
  status: "pending" | "in_progress" | "complete";
  summary?: string;
  fieldsCollected: number;
  fieldsTotal: number;
  appearsWhen: "first_field" | "always";
}

// ---------------------------------------------------------------------------
// Computation
// ---------------------------------------------------------------------------

export function computeJourneySections(
  sectionConfig: JourneySectionConfig[],
  specFields: SpecField[],
  answered: Record<string, unknown>,
): JourneySectionProgress[] {
  return sectionConfig.map((section) => {
    const sectionFields = specFields.filter(
      (f) => f.group && section.groups.includes(f.group) && f.required,
    );

    const fieldsTotal = sectionFields.length;
    const fieldsCollected = sectionFields.filter((f) => f.id in answered).length;

    let status: JourneySectionProgress["status"] = "pending";
    if (fieldsCollected > 0 && fieldsCollected < fieldsTotal) {
      status = "in_progress";
    } else if (fieldsTotal > 0 && fieldsCollected >= fieldsTotal) {
      status = "complete";
    }

    const summary =
      section.summaryFields && fieldsCollected > 0
        ? deriveSectionSummary(section.summaryFields, answered, specFields)
        : undefined;

    return {
      id: section.id,
      label: section.label,
      pendingText: section.pendingText,
      status,
      summary,
      fieldsCollected,
      fieldsTotal,
      appearsWhen: section.appearsWhen ?? "first_field",
    };
  });
}

export function deriveSectionSummary(
  summaryFields: string[],
  answered: Record<string, unknown>,
  specFields: SpecField[],
): string | undefined {
  const parts: string[] = [];

  for (const fieldId of summaryFields) {
    const value = answered[fieldId];
    if (value === undefined || value === null) continue;

    const spec = specFields.find((f) => f.id === fieldId);
    const formatted = formatFieldValue(value, spec);
    if (formatted) parts.push(formatted);
  }

  return parts.length > 0 ? parts.join("; ") : undefined;
}

function formatFieldValue(value: unknown, spec?: SpecField): string | undefined {
  if (value === undefined || value === null || value === "") return undefined;

  if (Array.isArray(value)) {
    const labels = value.map((v) => resolveChoiceLabel(String(v), spec));
    return labels.length > 0 ? labels.join(", ") : undefined;
  }

  if (typeof value === "string") {
    return resolveChoiceLabel(value, spec);
  }

  return String(value);
}

function resolveChoiceLabel(choiceId: string, spec?: SpecField): string {
  if (spec?.choices) {
    const choice = spec.choices.find((c) => c.id === choiceId);
    if (choice?.label) return choice.label;
    if (choice?.text) return choice.text;
  }
  return toTitleCase(choiceId);
}

function toTitleCase(s: string): string {
  return s.replace(/[_-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
